<template>
	<div class="contact-details-container">
		<div role="radiogroup" :aria-label="radioGroupAriaLabel">
			<BaseRadio
				:click="radioLoginClick"
				:name="radioLoginName"
				:activeClass="radioLoginActive"
				:aria-checked="radioLoginChecked"
				:ariaLabel="radioLoginAriaLabel"/>
			<div class="contact-form" v-if="radioLoginChecked">
				<template v-if="!isLoggedIn">
					<div class="contact-field" :class="updatePhoneClass()">
						<BaseIcon
						class="input-field-icon icon-mobile"
						icon="mobile"
						:isCustom="true"
						size="1x"/>
						<input class="input text-input has-icon" type="text" placeholder="Mobile Number" v-model="phone"/>
						<BaseButton
							:classNames="['button--modifier', 'input-field-button']"
							title="Login/Sign-up"
							type="fill"
							@click="showLogin"/>
					</div>
				</template>
				<div class="details-set-container" v-if="isLoggedIn">
					<div class="detail-info">
						<label for="login-customer-name">Name: </label>
						<span id="login-customer-name">{{ customer.printFullName() }}</span>
					</div>
					<div class="detail-info">
						<label for="login-customer-phone">Phone: </label>
						<span id="login-customer-phone">{{ printPhoneNumber(customer.phone) }}</span>
					</div>
					<div class="detail-info pad-top">
						<span id="logout-customer">
							<a href="/logout" @click.prevent="logoutAction">Sign out</a>
						</span>
					</div>
				</div>
			</div>
			<BaseRadio v-if="guestRadioEnabled"
				:click="radioGuestOrderClick"
				name="Guest"
				:activeClass="radioGuestOrderActive"
				:aria-checked="radioGuestOrderChecked"
				ariaLabel="Complete order as a guest"/>
			<div class="contact-form" v-if="guestFormEnabled">
				<div class="contact-field" :class="validateClass(hasValidName(guestNameModel))">
					<BaseIcon
						class="input-field-icon"
						icon="userEdit"
						size="1x"/>
					<input class="input text-input has-icon" type="text" placeholder="Name" v-model="guestNameModel" @blur="updateName()" />
				</div>
				<div class="contact-field" :class="validateClass(hasValidEmail(guestEmailModel))">
					<BaseIcon
						class="input-field-icon"
						icon="email"
						size="1x"/>
					<input class="input text-input has-icon" type="email" placeholder="Email" v-model="guestEmailModel" @blur="updateEmail()" />
				</div>
				<div class="contact-field" :class="updatePhoneClass()">
					<BaseIcon
						class="input-field-icon"
						icon="phone"
						size="1x"/>
					<input class="input text-input has-icon" type="tel" placeholder="Phone (607) 123 4567" v-model="phone" @blur="updatePhone()" />
				</div>
				<br>
				<p class="is-size-7 has-text-grey-light">
					By clicking on the "Place Order" button, you agree to our
					<a class="has-text-grey-light" target="_blank" href="https://www.touchpoint.io/privacy.php">
						Terms of Service
					</a>
					and
					<a class="has-text-grey-light" target="_blank" href="https://www.touchpoint.io/privacy.php">
						Privacy Policy
					</a>
				</p>
			</div>
			<transition name="fade" mode="in-out">
				<p v-if="errorMessage && !isLoggedIn" class="error">{{ errorMessage }}</p>
			</transition>
		</div>
	</div>
</template>

<script>
	import EventBus from '@EventBus';
	import { ACCOUNT, ACCOUNT_CHECKOUT, LOCATION } from "@/stores/defs";
	import { mapActions, mapGetters } from 'vuex';
	import guestService from '@/services/guest.service';
	import { isEmpty } from 'lodash';
	import phoneValidatorMixin from '@/components/mixins/phoneValidatorMixin';
	import checkoutMixin from '@/components/mixins/checkoutMixin';
	import { formatNumber, isValidNumber, AsYouType } from 'libphonenumber-js';
	import Login from '@/components/auth/Login.vue';
	import modalMixin from '@/components/mixins/modalMixin';
	import logoutMixin from '@/components/mixins/logoutMixin';
	import featureMixin from '@/components/mixins/featureMixin';
	import Features from '@/utils/Features';
	import customerService from '@/services/customer.service';
	import localStorageAdapter from '@/utils/LocalStorageAdapter';
	const inputStates = {
		'error': 'error',
		'success': 'success',
		'empty': '',
	}
	export default {
		mixins: [
			phoneValidatorMixin,
			checkoutMixin,
			modalMixin,
			logoutMixin,
			featureMixin
		],
		data() {
			return {
				guestNameModel: '',
				guestEmailModel: '',
				correctionsNeeded: null,
				errorMessage: '',
			}
		},
		computed: {
			...mapGetters({
				isGuest: ACCOUNT_CHECKOUT.GET_IS_GUEST,
				guestId: ACCOUNT_CHECKOUT.GET_GUEST_ID,
				guestPhone: ACCOUNT.GET_GUEST_PHONE,
				guestEmail: ACCOUNT.GET_GUEST_EMAIL,
				guestName: ACCOUNT.GET_GUEST_NAME,
				customer: ACCOUNT.GET_CUSTOMER,
				loggedIn: ACCOUNT.GET_LOGGED_IN,
				location: LOCATION.GET_LOCATION,
			}),
			guestRadioEnabled() {
				const noUser = !this.isLoggedIn;
				const featureEnabled = this.guestCheckoutEnabled();
				return featureEnabled && noUser;
			},
			guestFormEnabled() {
				const formSelected = this.radioGuestOrderChecked;
				const featureEnabled = this.guestCheckoutEnabled();
				return featureEnabled && formSelected;
			},
			isLoggedIn() {
				const customer = this.customer;
				if (!isEmpty(customer.id) && this.loggedIn) {
					return true;
				}
				return false;
			},
			radioGroupAriaLabel() {
				return 'Select contact details, login or checkout as a guest';
			},
			radioLoginName() {
				const appConstants = this.$appConstants;
				const storeName = appConstants.storeName;
				let name = '';
				if (this.loggedIn) {
					name = ` (${this.customer.firstName})`;
				}
				return `Account${name}`;
			},
			radioLoginChecked() {
				return (!this.isGuest);
			},
			radioLoginAriaLabel() {
				return `${this.radioLoginName}`;
			},
			radioLoginActive() {
				if (this.radioLoginChecked) {
					return 'selected';
				}
				return '';
			},
			radioGuestOrderChecked() {
				return (this.isGuest && !this.loggedIn);
			},
			radioGuestOrderActive() {
				if (this.radioGuestOrderChecked) {
					return 'selected';
				}
				return '';
			},
		},
		methods: {
			...mapActions({
				setIsGuest: ACCOUNT_CHECKOUT.SET_IS_GUEST,
				setGuestId: ACCOUNT_CHECKOUT.SET_GUEST_ID,
				setGuestPhone: ACCOUNT.SET_GUEST_PHONE,
				setGuestEmail: ACCOUNT.SET_GUEST_EMAIL,
				setGuestName: ACCOUNT.SET_GUEST_NAME,
				updateCustomer: ACCOUNT.UPDATE_CUSTOMER,
			}),
			guestCheckoutEnabled() {
				const guestCheckout = this.checkForFeature(Features.GUEST_CHECKOUT);
				return guestCheckout;
			},
			radioLoginClick() {
				this.errorMessage = '';
				this.setIsGuest(false);
			},
			radioGuestOrderClick() {
				this.setIsGuest(true);
			},
			validateClass(state) {
				return inputStates[state];
			},
			updatePhoneClass() {
				const result = this.liveValidate;
				const valid = this.hasValidPhone(this.guestPhone);
				return valid;
			},
			printPhoneNumber(phoneNumber) {
				const ayt = new AsYouType('US');
				ayt.input(phoneNumber);
				const formatted = ayt.formattedOutput;
				return `${formatted}`;
			},
			showLogin() {
				const skipToPin = (this.liveValidate);
				this.triggerModal({
					component: Login,
					title: "Login",
					classes: 'global-modal',
					modalName: "loginModal",
					contentProperties: {
						routeTo: '/checkout',
						skipToPin: skipToPin,
					},
				});
			},
			logoutAction() {
				this.logout('/checkout');
				EventBus.$emit('logout');
			},
			updatePhone() {
				this.updateCustomer({
					phone: this.guestPhone,
				});
				this.saveProperties();
			},
			updateEmail() {
				this.updateCustomer({
					email: this.guestEmailModel,
				});
				this.saveProperties();
			},
			updateName() {
				this.updateCustomer({
					firstName: this.guestNameModel,
				});
				this.saveProperties();
			},
			saveProperties() {
				let apiReq = {
					lb: this.location.id,
					cmd: 'guest_update',
					c: {
						base: this.guestId,
						phone: this.guestPhone,
						email: this.guestEmail,
						firstname: this.guestName,
					}
				};
				customerService.editProfile(apiReq).then((response) => {
					// Guest saved
					this.errorMessage = '';
					if (this.validForms()) {
						this.correctionsNeeded = null;
						this.storeGuestInfo();
					}
				}).catch((err) => {
					console.error(`Error saving guest information: ${err}`);
				});
			},
			storeGuestInfo() {
				const guestInfo = {
					guestEmail: this.guestEmailModel,
					guestName: this.guestNameModel,
				};
				localStorageAdapter.setItem('guest-info', guestInfo);
			},
			retrieveGuestInfo() {
				const guestInfo = localStorageAdapter.getItem('guest-info');
				if (!guestInfo) {
					return;
				}
				this.setGuestName(guestInfo.guestName);
				this.setGuestEmail(guestInfo.guestEmail);
			},
			formInvalidAlert(fields) {
				this.correctionsNeeded = fields;
			},
			hasError() {
				return !(isEmpty(this.errorMessage));
			},
			showErrors() {
				this.handleErrorMessage();
			},
			handleErrorMessage() {
				const error = this.correctionsNeeded;
				if (!error) {
					return;
				}
				const name = error.name;
				this.errorMessage = `Oops, please enter a valid ${name}.`;
			},
			validForms() {
				return !this.missingInformation();
			}
		},
		mounted() {
			this.setGuestName(this.guestName);
			this.setGuestEmail(this.guestEmail);
			this.guestNameModel = this.guestName;
			this.guestEmailModel = this.guestEmail;
			this.phone = this.guestPhone;
		},
		created() {
			const customer = this.customer;
			const vue = this;
			const locationBase = this.location.id;
			const noGuestId = isEmpty(this.guestId);
			const hasGuestId = !noGuestId;
			this.setFeatureFlag({
				name: Features.GUEST_CHECKOUT,
				flag: hasGuestId,
			});
			if (noGuestId && !customer.id) {
				guestService.fetchGuestCustomer(locationBase, ({ customerId, success }) => {
					if (!success) {
						return;
					}
					this.setFeatureFlag({
						name: Features.GUEST_CHECKOUT,
						flag: true,
					});
					this.updateCustomer({
						paymentAccounts: [],
					});
					vue.setGuestId(customerId);
					EventBus.$emit('customer-update');
				});
			}
			EventBus.$on('form-invalid-alert', this.formInvalidAlert);
			EventBus.$on('show-errors', this.showErrors);
			this.retrieveGuestInfo();
		},
		destroyed() {
			EventBus.$off('form-invalid-alert');
			EventBus.$off('show-errors');
		},
		watch: {
			guestEmailModel(newValue, oldValue) {
				if (newValue !== oldValue) {
					this.setGuestEmail(newValue);
				}
			},
			guestNameModel(newValue, oldValue) {
				if (!newValue) { return; }
				if (newValue !== oldValue) {
					if (newValue.trim() === '') {
						this.setGuestName('-');
						return;
					}
					this.setGuestName(newValue);
				}
			},
			phone(newValue, oldValue) {
				if (newValue !== oldValue) {
					const newIsValid = isValidNumber(newValue);
					const oldIsValid = oldValue && isValidNumber(oldValue);
					if ( newIsValid && oldIsValid) {
						return;
					}
					let formatted = newValue;
					if (!newIsValid && newValue !== '') {
						formatted = formatNumber(newValue, 'US', 'National');
					}
					this.setGuestPhone(formatted);
				}
			}
		},
	}
</script>

<style lang="scss" scoped>
@import "globalStyles";
@import "commonStyle/contactForm";
</style>


// WEBPACK FOOTER //
// src/components/account/ContactDetails/ContactDetails.vue